import { useRef } from 'react';

import LucideSymbolIcon from '../../icons/LucideSymbolIcon';
import { authService } from '../../../services/authService';
import { resolveBrandingLogoUrl } from '../../../utils/brandingUrl';

type Props = {
  label?: string;
  value: string;
  onChange: (url: string) => void;
  inputClass: string;
  onUploadError?: (message: string) => void;
  helperText?: string;
  compact?: boolean;
};

export default function AdminCatalogImageField({
  label = 'Image de couverture',
  value,
  onChange,
  inputClass,
  onUploadError,
  helperText = 'Bannière affichée sur le catalogue web et mobile (PNG, JPEG ou WebP).',
  compact = false,
}: Props) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const previewSrc = resolveBrandingLogoUrl(value);

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    try {
      const uploadedUrl = await authService.uploadProfileImage(file);
      onChange(uploadedUrl);
    } catch {
      onUploadError?.('Téléversement de l’image impossible.');
    }
  };

  if (compact) {
    return (
      <div className="min-w-0">
        <span className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-700">{label}</span>
        {helperText ? <p className="mb-2 text-[11px] leading-snug text-on-surface-variant">{helperText}</p> : null}

        <div className="flex flex-wrap items-center gap-3 rounded-xl border border-outline-variant/20 bg-white p-2.5 shadow-sm">
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="group relative flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-outline-variant/20 bg-gradient-to-br from-slate-100 to-slate-50"
            aria-label={previewSrc ? 'Remplacer le logo' : 'Ajouter un logo'}
          >
            {previewSrc ? (
              <img src={previewSrc} alt="" className="h-full w-full object-contain p-1.5" />
            ) : (
              <LucideSymbolIcon name="add_photo_alternate" tone="primary" size={22} />
            )}
          </button>

          <div className="min-w-0 flex-1 space-y-2">
            <label className="block min-w-0">
              <span className="sr-only">URL de l’image</span>
              <input
                className={inputClass}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                placeholder="https://… ou /uploads/public/…"
              />
            </label>
            <div className="flex flex-wrap gap-1.5">
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="inline-flex items-center gap-1.5 rounded-lg border border-primary/25 bg-primary/5 px-2.5 py-1.5 text-[11px] font-bold text-primary hover:bg-primary/10"
              >
                <LucideSymbolIcon name="upload" tone="primary" size={14} />
                Téléverser
              </button>
              {value ? (
                <button
                  type="button"
                  onClick={() => onChange('')}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-outline-variant/30 bg-white px-2.5 py-1.5 text-[11px] font-semibold text-red-700 hover:bg-red-50"
                >
                  <LucideSymbolIcon name="delete" tone="inherit" size={14} className="text-red-600" />
                  Retirer
                </button>
              ) : null}
            </div>
          </div>
        </div>

        <input
          ref={fileInputRef}
          type="file"
          accept="image/png,image/jpeg,image/webp"
          className="hidden"
          onChange={(e) => void handleFileChange(e)}
        />
      </div>
    );
  }

  return (
    <div className="min-w-0">
      <span className="mb-1.5 block text-xs font-bold uppercase tracking-wide text-slate-700">{label}</span>
      <p className="mb-3 text-[11px] text-on-surface-variant">{helperText}</p>

      <div className="overflow-hidden rounded-2xl border border-outline-variant/20 bg-white shadow-sm">
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          className="group relative block w-full text-left"
        >
          <div className="relative aspect-[21/9] w-full overflow-hidden bg-gradient-to-br from-slate-100 to-slate-50">
            {previewSrc ? (
              <img
                src={previewSrc}
                alt=""
                className="h-full w-full object-cover transition group-hover:scale-[1.02]"
              />
            ) : (
              <div className="flex h-full flex-col items-center justify-center gap-2 px-4 text-center">
                <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white shadow-md">
                  <LucideSymbolIcon name="add_photo_alternate" tone="primary" size={26} />
                </span>
                <span className="text-sm font-semibold text-slate-700">Cliquez pour ajouter une image</span>
                <span className="text-xs text-on-surface-variant">ou collez une URL ci-dessous</span>
              </div>
            )}
            <div className="absolute inset-0 bg-slate-900/0 transition group-hover:bg-slate-900/10" aria-hidden />
            {previewSrc ? (
              <span className="absolute bottom-3 right-3 rounded-lg bg-white/95 px-3 py-1.5 text-xs font-bold text-primary shadow-md opacity-0 transition group-hover:opacity-100">
                Remplacer l’image
              </span>
            ) : null}
          </div>
        </button>

        <div className="space-y-3 border-t border-outline-variant/15 bg-surface-container-low/30 p-4">
          <label className="block min-w-0">
            <span className="sr-only">URL de l’image</span>
            <input
              className={inputClass}
              value={value}
              onChange={(e) => onChange(e.target.value)}
              placeholder="https://… ou /uploads/public/…"
            />
          </label>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="inline-flex items-center gap-2 rounded-xl border border-primary/25 bg-primary/5 px-3.5 py-2 text-xs font-bold text-primary hover:bg-primary/10"
            >
              <LucideSymbolIcon name="upload" tone="primary" size={16} />
              Téléverser
            </button>
            {value ? (
              <button
                type="button"
                onClick={() => onChange('')}
                className="inline-flex items-center gap-2 rounded-xl border border-outline-variant/30 bg-white px-3.5 py-2 text-xs font-semibold text-red-700 hover:bg-red-50"
              >
                <LucideSymbolIcon name="delete" tone="inherit" size={16} className="text-red-600" />
                Retirer
              </button>
            ) : null}
          </div>
        </div>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        className="hidden"
        onChange={(e) => void handleFileChange(e)}
      />
    </div>
  );
}
